import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { type Heading } from '~/utils/extractHeadings';
import * as styles from './Outline.css';

type Props = {
  headings: Heading[];
};

export const Outline = ({ headings }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const manualActiveIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (manualActiveIdRef.current) return;

      if (window.scrollY < 1) {
        setActiveId(null);
        return;
      }

      const scrollY = window.scrollY;
      const isBottom = scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;

      const headers = headings
        .map((h) => {
          const element = document.getElementById(h.id);
          if (!element) return null;
          return {
            id: h.id,
            top: element.getBoundingClientRect().top + window.scrollY,
          };
        })
        .filter((item): item is { id: string; top: number } => item !== null && !Number.isNaN(item.top));

      if (!headers.length) {
        setActiveId(null);
        return;
      }

      if (isBottom) {
        setActiveId(headers.at(-1)?.id ?? null);
        return;
      }

      const scrollOffset = 64;
      let newActiveId: string | null = null;
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        if (!header) continue;
        const { id, top } = header;
        const offset = i === 0 ? scrollOffset * 2 : scrollOffset;
        if (top > scrollY + offset) break;
        newActiveId = id;
      }

      setActiveId(newActiveId);
    };

    const onScroll = () => {
      manualActiveIdRef.current = null;

      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      throttleTimeoutRef.current = setTimeout(handleScroll, 100);
    };

    handleScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [headings]);

  useEffect(() => {
    if (!containerRef.current || !markerRef.current) return;

    const activeLink = activeId
      ? containerRef.current.querySelector<HTMLAnchorElement>(`a[href="#${activeId}"]`)
      : null;

    if (activeLink) {
      markerRef.current.style.top = `${activeLink.offsetTop}px`;
      markerRef.current.style.opacity = '1';
      markerRef.current.style.visibility = 'visible';
    } else {
      markerRef.current.style.opacity = '0';
      markerRef.current.style.visibility = 'hidden';
    }
  }, [activeId]);

  if (headings.length === 0) return null;

  const minLevel = Math.min(...headings.map((h) => h.level));

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      window.history.pushState(null, '', `#${id}`);

      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      if (scrollHeight <= clientHeight) {
        manualActiveIdRef.current = id;
        setActiveId(id);
      }
    }
  };
  return (
    <aside ref={containerRef} className={styles.outline}>
      <div className={styles.outlineMarker} ref={markerRef} />
      <div className={styles.outlineHeader}>On this page</div>
      <nav className={styles.outlineNav}>
        {headings.map((heading) => (
          <Link
            key={heading.id}
            href={`#${heading.id}`}
            className={styles.outlineLink}
            data-level={heading.level - minLevel + 1}
            data-active={activeId === heading.id ? 'true' : undefined}
            onClick={(e) => handleClick(e, heading.id)}
          >
            {heading.text}
          </Link>
        ))}
      </nav>
    </aside>
  );
};
